import React, { useRef, useState } from "react";
import { GripVertical } from "lucide-react";

import { cn } from "../lib/utils";

const LIMIAR_ARRASTO = 6;

/**
 * Ordem de agrupamento da tabela: arraste para reordenar, clique para ocultar.
 *
 * Usa eventos de PONTEIRO, e não o arrastar nativo do HTML5: o nativo não funciona em toque, e o
 * comercial usa tablet. Com ponteiro, mouse e dedo seguem o mesmo caminho.
 *
 * Arrastar e clicar convivem no mesmo gesto: só vira arrasto depois de andar alguns pixels; abaixo
 * disso é clique, que liga ou desliga a dimensão.
 */
export default function OrdemAgrupamento({ dimensoes, ordem, onChange, padrao }) {
  const [arrastando, setArrastando] = useState(null);
  const refs = useRef(new Map());
  const gesto = useRef(null);

  const ativas = ordem;
  const inativas = Object.keys(dimensoes).filter((chave) => !ordem.includes(chave));

  const alternar = (chave) => {
    if (ordem.includes(chave)) {
      const proxima = ordem.filter((item) => item !== chave);
      // Sem nenhuma dimensão a tabela não teria o que agrupar: a última não sai.
      if (proxima.length) onChange(proxima);
      return;
    }
    onChange([...ordem, chave]);
  };

  const posicaoAlvo = (clientX) => {
    const centros = ativas.map((chave) => {
      const elemento = refs.current.get(chave);
      if (!elemento) return Number.POSITIVE_INFINITY;
      const caixa = elemento.getBoundingClientRect();
      return caixa.left + caixa.width / 2;
    });
    const indice = centros.findIndex((centro) => clientX < centro);
    return indice === -1 ? ativas.length - 1 : indice;
  };

  const aoPressionar = (evento, chave) => {
    if (!ordem.includes(chave)) return;
    evento.currentTarget.setPointerCapture?.(evento.pointerId);
    gesto.current = { chave, x: evento.clientX, arrastou: false };
  };

  const aoMover = (evento) => {
    const atual = gesto.current;
    if (!atual) return;
    if (!atual.arrastou && Math.abs(evento.clientX - atual.x) < LIMIAR_ARRASTO) return;

    atual.arrastou = true;
    setArrastando(atual.chave);
    const destino = posicaoAlvo(evento.clientX);
    const origem = ordem.indexOf(atual.chave);
    if (destino === origem || destino < 0) return;

    const proxima = [...ordem];
    proxima.splice(origem, 1);
    proxima.splice(destino, 0, atual.chave);
    onChange(proxima);
  };

  const aoSoltar = (chave) => {
    const atual = gesto.current;
    gesto.current = null;
    setArrastando(null);
    if (!atual?.arrastou) alternar(chave);
  };

  const aoTeclar = (evento, chave) => {
    if (evento.key === "Enter" || evento.key === " ") {
      evento.preventDefault();
      alternar(chave);
      return;
    }
    // Teclado move a dimensão sem arrastar: quem navega por teclado também reordena.
    const direcao = evento.key === "ArrowRight" ? 1 : evento.key === "ArrowLeft" ? -1 : 0;
    if (!direcao || !ordem.includes(chave)) return;
    evento.preventDefault();
    const origem = ordem.indexOf(chave);
    const destino = origem + direcao;
    if (destino < 0 || destino >= ordem.length) return;
    const proxima = [...ordem];
    proxima.splice(origem, 1);
    proxima.splice(destino, 0, chave);
    onChange(proxima);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-muted-foreground">Agrupar por:</span>

      {ativas.map((chave, posicao) => (
        <button
          key={chave}
          type="button"
          ref={(elemento) => {
            if (elemento) refs.current.set(chave, elemento);
            else refs.current.delete(chave);
          }}
          onPointerDown={(evento) => aoPressionar(evento, chave)}
          onPointerMove={aoMover}
          onPointerUp={() => aoSoltar(chave)}
          onPointerCancel={() => { gesto.current = null; setArrastando(null); }}
          onKeyDown={(evento) => aoTeclar(evento, chave)}
          aria-pressed="true"
          title="Arraste para mudar a ordem · clique para ocultar"
          className={cn(
            "inline-flex min-h-9 touch-none select-none items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition",
            "border-foreground/30 bg-accent text-foreground",
            arrastando === chave ? "scale-[1.03] shadow-md" : "cursor-grab"
          )}
        >
          <GripVertical className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          <span className="grid h-4 w-4 place-items-center rounded-full bg-foreground text-[10px] font-bold text-background">
            {posicao + 1}
          </span>
          {dimensoes[chave].label}
        </button>
      ))}

      {inativas.map((chave) => (
        <button
          key={chave}
          type="button"
          onClick={() => alternar(chave)}
          aria-pressed="false"
          title="Clique para incluir no agrupamento"
          className="inline-flex min-h-9 items-center rounded-full border border-dashed border-border px-3 text-xs font-medium text-muted-foreground transition hover:text-foreground"
        >
          {dimensoes[chave].label}
        </button>
      ))}

      {padrao && ordem.join() !== padrao.join() ? (
        <button
          type="button"
          onClick={() => onChange(padrao)}
          className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          Voltar ao padrão
        </button>
      ) : null}
    </div>
  );
}
